import { useCallback, useEffect, useMemo } from 'react'
import {
  SaturatedItem,
  selectSaturatedBreadcrumbsItems,
  setBreadcrumbs as setBreadcrumbsAction,
  TreeNodeOrNodeSlug,
} from 'react-common'
import { useTranslation } from 'react-i18next'
import { generatePathWithRoot } from '@/features/layout/utils/generatePathWithRoot'
import { useIsCatalogCourse } from '@/hooks/useIsCatalogCourse'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { useCoursesTitle } from './useCoursesTitle'

export const useBreadcrumbs = () => {
  const { t } = useTranslation()
  const isCatalogCourse = useIsCatalogCourse()
  const coursesTitle = useCoursesTitle()

  const dispatch = useAppDispatch()
  const saturatedBreadcrumbs = useAppSelector(selectSaturatedBreadcrumbsItems)

  const rootBreadcrumbItem = useMemo(() => {
    const catalogRoot = {
      name: t('catalogList.heading'),
      slug: 'catalog-list',
      url: generatePathWithRoot('/catalog'),
    } as TreeNodeOrNodeSlug

    const enrolledCoursesRoot = {
      name: coursesTitle,
      slug: 'courses-list',
      url: generatePathWithRoot('/courses'),
    } as TreeNodeOrNodeSlug

    return isCatalogCourse ? catalogRoot : enrolledCoursesRoot
  }, [t, isCatalogCourse, coursesTitle])

  const setBreadcrumbs = useCallback(
    (breadcrumbs: TreeNodeOrNodeSlug[] | null) => {
      if (breadcrumbs === null) {
        dispatch(setBreadcrumbsAction([]))
        return
      }
      dispatch(setBreadcrumbsAction(breadcrumbs))
    },
    [dispatch],
  )

  const setMyCourseBreadcrumbs = useCallback(
    (breadcrumbs: TreeNodeOrNodeSlug[]) => {
      setBreadcrumbs([rootBreadcrumbItem, ...(breadcrumbs ?? [])])
    },
    [rootBreadcrumbItem, setBreadcrumbs],
  )

  useEffect(() => {
    return () => {
      setBreadcrumbs(null)
    }
  }, [setBreadcrumbs])

  return {
    saturatedBreadcrumbs: saturatedBreadcrumbs as Map<string, SaturatedItem>,
    setBreadcrumbs,
    setMyCourseBreadcrumbs,
  }
}
